// ─────────────────────────────────────────────────────────────────────────────
// wizard_scroll.ts — Interactive margin dialogue scroll
//
// When a wizard-type arcane channel is active, this module renders a dialogue
// scroll in the right margin of the page.  The scroll shows the conversation
// history between the user and Ireth Vaun, drawing each turn as alternating
// handwritten-style lines.
//
// All rendering is Canvas 2D — no DOM elements.
// ─────────────────────────────────────────────────────────────────────────────

import type { WvMarginNote } from './notebook_state';
import type { PageLayout } from './page_renderer';

// ── Visual constants ──────────────────────────────────────────────────────────

const SCROLL_WIDTH       = 200;
const SCROLL_MARGIN      = 12;     // gap between page edge and scroll
const SCROLL_PADDING     = 10;
const SCROLL_BG          = 'rgba(245, 235, 210, 0.92)';
const SCROLL_BORDER      = 'rgba(139, 99, 64, 0.35)';
const SCROLL_SHADOW      = 'rgba(0, 0, 0, 0.20)';
const HEADER_FONT        = 'italic small-caps 12px Georgia, serif';
const BODY_FONT          = 'italic 11px Georgia, serif';
const USER_FONT          = '11px Georgia, serif';
const LINE_HEIGHT        = 14;
const TURN_GAP           = 8;

const GLYPH_COLORS: Record<string, string> = {
  calm: '#408050',
  warning: '#c08020',
  archaic: '#5080c0',
  dismissive: '#c23030',
};

const GLYPH_ICONS: Record<string, string> = {
  calm: '⊕', warning: '↯', archaic: '☿', dismissive: '⊗',
};

// ── Dialogue turn ─────────────────────────────────────────────────────────────

export interface ScrollTurn {
  speaker: 'user' | 'wizard';
  text: string;
  glyphStyle?: string;
  timestamp: number;
}

// ── WizardScroll class ────────────────────────────────────────────────────────

export class WizardScroll {
  private _turns: ScrollTurn[] = [];
  private _scrollOffset = 0;    // pixels scrolled up (positive = scrolled up)
  private _visible = false;
  private _anchorBlockId: string | undefined;
  private _animReveal = 0;      // 0→1 entrance animation
  private _totalContentH = 0;   // computed in last draw

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /** Open the scroll for a wizard arcane channel. */
  open(anchorBlockId: string): void {
    this._anchorBlockId = anchorBlockId;
    this._visible = true;
    this._animReveal = 0;
    this._scrollOffset = 0;
    this._turns = [];
  }

  /** Close the scroll dialogue. */
  close(): void {
    this._visible = false;
    this._anchorBlockId = undefined;
  }

  get visible(): boolean { return this._visible; }
  get anchorBlockId(): string | undefined { return this._anchorBlockId; }

  // ── Content ───────────────────────────────────────────────────────────────

  /** Add a user query turn. */
  addUserTurn(text: string): void {
    this._turns.push({ speaker: 'user', text, timestamp: Date.now() });
    this._scrollToBottom();
  }

  /** Add a wizard response turn from a margin note. */
  addWizardTurn(note: WvMarginNote): void {
    this._turns.push({
      speaker: 'wizard',
      text: note.text,
      glyphStyle: note.glyphStyle,
      timestamp: note.createdAt,
    });
    this._scrollToBottom();
  }

  /** Rebuild dialogue from persisted marginalia (on page load). */
  rebuildFromMarginalia(notes: WvMarginNote[], anchorBlockId: string): void {
    this._turns = [];
    // Marginalia are wizard turns; intersperse with synthetic user turns
    const sorted = [...notes]
      .filter(n => n.anchorBlockId === anchorBlockId)
      .sort((a, b) => a.createdAt - b.createdAt);

    for (const n of sorted) {
      this._turns.push({
        speaker: 'wizard',
        text: n.text,
        glyphStyle: n.glyphStyle,
        timestamp: n.createdAt,
      });
    }
    this._scrollToBottom();
  }

  // ── Scrolling ─────────────────────────────────────────────────────────────

  scroll(deltaY: number): void {
    if (!this._visible) { return; }
    this._scrollOffset = Math.max(0, this._scrollOffset + deltaY);
    // Clamp to content
    const maxScroll = Math.max(0, this._totalContentH - 300);
    this._scrollOffset = Math.min(this._scrollOffset, maxScroll);
  }

  private _scrollToBottom(): void {
    // Next draw will cap it; set a high value to trigger auto-scroll
    this._scrollOffset = Math.max(0, this._totalContentH - 300);
  }

  // ── Hit testing ───────────────────────────────────────────────────────────

  /** Returns the scroll bounds in canvas coordinates, or null if hidden. */
  getBounds(layout: PageLayout): { x: number; y: number; w: number; h: number } | null {
    if (!this._visible) { return null; }
    const sx = layout.x + layout.width + SCROLL_MARGIN;
    const sy = layout.y + layout.marginTop;
    const sw = SCROLL_WIDTH;
    const sh = layout.height - layout.marginTop - layout.marginBottom;
    return { x: sx, y: sy, w: sw, h: sh };
  }

  /** Check if canvas coordinates fall inside the scroll region. */
  hitTest(cx: number, cy: number, layout: PageLayout): boolean {
    const b = this.getBounds(layout);
    if (!b) { return false; }
    return cx >= b.x && cx < b.x + b.w && cy >= b.y && cy < b.y + b.h;
  }

  // ── Draw ──────────────────────────────────────────────────────────────────

  draw(
    ctx: CanvasRenderingContext2D,
    layout: PageLayout,
    t: number,
  ): void {
    if (!this._visible) { return; }

    // Entrance animation
    if (this._animReveal < 1) {
      this._animReveal = Math.min(1, this._animReveal + 0.04);
    }
    const reveal = this._animReveal;

    const b = this.getBounds(layout)!;
    const { x, y, w, h } = b;

    ctx.save();

    // Draw the scroll parchment background
    ctx.globalAlpha = reveal;
    ctx.shadowColor = SCROLL_SHADOW;
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;

    // Scroll parchment shape — slightly rounded corners
    this._roundRect(ctx, x, y, w, h, 4);
    ctx.fillStyle = SCROLL_BG;
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Border
    this._roundRect(ctx, x, y, w, h, 4);
    ctx.strokeStyle = SCROLL_BORDER;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Decorative header
    ctx.font = HEADER_FONT;
    ctx.fillStyle = 'rgba(100, 80, 50, 0.7)';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText('☿  Wizard\'s Scroll  ☿', x + w / 2, y + 6);
    ctx.textAlign = 'left';

    // Divider line under header
    const headerH = 24;
    ctx.beginPath();
    ctx.moveTo(x + SCROLL_PADDING, y + headerH);
    ctx.lineTo(x + w - SCROLL_PADDING, y + headerH);
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.2)';
    ctx.stroke();

    // Clip to content area
    const contentX = x + SCROLL_PADDING;
    const contentY = y + headerH + 4;
    const contentW = w - SCROLL_PADDING * 2;
    const contentH = h - headerH - 8;

    ctx.beginPath();
    ctx.rect(contentX, contentY, contentW, contentH);
    ctx.clip();

    // Draw turns with scroll offset
    let cursorY = contentY - this._scrollOffset;
    const maxTextW = contentW - 14; // leave room for glyph

    for (const turn of this._turns) {
      if (turn.speaker === 'wizard') {
        // Wizard turn — italic with glyph icon
        const style = turn.glyphStyle ?? 'calm';
        const icon = GLYPH_ICONS[style] ?? '·';
        const color = GLYPH_COLORS[style] ?? '#805030';

        // Glyph icon
        if (cursorY + LINE_HEIGHT > contentY && cursorY < contentY + contentH) {
          ctx.font = '9px Georgia';
          ctx.fillStyle = color;
          ctx.fillText(icon, contentX, cursorY + 2);
        }

        // Wrap and draw text
        ctx.font = BODY_FONT;
        ctx.fillStyle = '#3a2510';
        const lines = this._wrapText(ctx, turn.text, maxTextW);
        for (const line of lines) {
          if (cursorY + LINE_HEIGHT > contentY && cursorY < contentY + contentH) {
            ctx.fillText(line, contentX + 12, cursorY);
          }
          cursorY += LINE_HEIGHT;
        }
      } else {
        // User turn — regular serif, dimmer
        if (cursorY + LINE_HEIGHT > contentY && cursorY < contentY + contentH) {
          ctx.font = '9px Georgia';
          ctx.fillStyle = 'rgba(80, 60, 30, 0.5)';
          ctx.fillText('✎', contentX, cursorY + 2);
        }

        ctx.font = USER_FONT;
        ctx.fillStyle = 'rgba(60, 40, 20, 0.7)';
        const lines = this._wrapText(ctx, turn.text, maxTextW);
        for (const line of lines) {
          if (cursorY + LINE_HEIGHT > contentY && cursorY < contentY + contentH) {
            ctx.fillText(line, contentX + 12, cursorY);
          }
          cursorY += LINE_HEIGHT;
        }
      }

      cursorY += TURN_GAP;
    }

    // Track total content height for scroll bounds
    this._totalContentH = cursorY + this._scrollOffset - contentY;

    // Scroll indicator (thin vertical bar on right)
    if (this._totalContentH > contentH) {
      const ratio = contentH / this._totalContentH;
      const barH = Math.max(20, contentH * ratio);
      const barY = contentY + (this._scrollOffset / this._totalContentH) * (contentH - barH);
      ctx.fillStyle = 'rgba(139, 99, 64, 0.25)';
      this._roundRect(ctx, x + w - 5, barY, 3, barH, 1.5);
      ctx.fill();
    }

    // Waiting animation: faint pulsing dots if last turn was from user
    if (this._turns.length > 0 && this._turns[this._turns.length - 1].speaker === 'user') {
      const dots = '· · ·';
      const pulse = 0.3 + 0.3 * Math.sin(t * 3);
      ctx.globalAlpha = pulse;
      ctx.font = BODY_FONT;
      ctx.fillStyle = '#805030';
      // Draw at bottom of content
      const dotY = Math.min(cursorY, contentY + contentH - LINE_HEIGHT);
      if (dotY > contentY) {
        ctx.fillText(dots, contentX + 12, dotY);
      }
    }

    ctx.restore();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private _wrapText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';
    for (const w of words) {
      const test = current ? `${current} ${w}` : w;
      if (ctx.measureText(test).width > maxW) {
        if (current) { lines.push(current); }
        current = w;
      } else {
        current = test;
      }
    }
    if (current) { lines.push(current); }
    return lines;
  }

  private _roundRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number,
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
}
